// v1.1.6 — a browser is bound to ONE wallet at a time (design-playbook,
// *The login contract*). When a login lands on a different wallet than the one
// this browser was bound to ("Use another wallet", a pay link, a claim), the
// browser rebinds: everything this browser keeps FOR a wallet is put aside
// under the old wallet's address, and the new wallet's own copy (if it was
// ever used here) is put back. Nothing is deleted, so switching back restores
// the old wallet exactly — its held first-payment rests and their claim keys
// included.
//
// Why it matters: settings sync is newest-wins per key (settings.ts). Without
// this, wallet B's emoji/theme/currencies — stamped newer on this browser —
// would be pushed INTO wallet A's profile at A's first login here, and B's
// sends, held rests and recents would show inside A.
//
// The device-wide keys stay as they are: the device flag itself (rewritten at
// login), m.cred (written by the caller), the profile cache of other people,
// motion/version/greeting housekeeping and m.loginMark.

import { initTheme } from "./theme";
import { releasePusher } from "./settings";
import { getEmoji } from "./emoji";
import { cachedProfile } from "./profileCache";

/** v1.1.9 — what this browser opened with before a login landed on another
 *  wallet: asked about once on the home ("Use 🦊 Lucy as your main wallet
 *  here?"). Device-wide, never stashed. */
export const ASK_KEY = "m.switchAsk";
/** v1.1.9 — the answer "No, keep 🐼": applied at the next lock screen, so the
 *  session in progress keeps its own wallet's state. */
export const REVERT_KEY = "m.revertTo";
export interface PrevWallet { address: string; cred: unknown; name: string | null; emoji: string; label: string | null; }

/** Everything this browser keeps that belongs to the bound wallet. */
const WALLET_KEYS = [
  // synced settings (settings.ts SYNCED) + their stamps
  "m.theme",
  "m.emoji",
  "m.currencies",
  "m.currency",
  "m.hintDrop2",
  "m.settings",
  "m.maskBalance", // 1.4.67 — "Mask my balance"
  "m.card", // 1.5.50 — the M® card's public record and the limit: the wallet's, synced
  "m.cardAbove",
  "m.emojiSynced",
  // device-only, but the wallet's: its sends, held rests (+ claim keys),
  // recent recipients and refs, nicknames it gave, its last known total
  "m.txlog",
  "m.held",
  "m.dropKeys",
  "m.recent",
  "m.recentRefs",
  "m.nick",
  "m.nickAlso",
  "m.lastTotal",
  "m.openCcy", // 1.5.41 — the pocket a note take left for the next home (fx.ts openHomeOn); 1.4.63's m.lastCcy is gone
  // v1.1.15 — M® Note: the notes this wallet sent from this browser, with
  // their codes and link secrets (notes.ts NOTES_KEY). Never synced.
  "m.notes",
] as const;

const STASH = (address: string) => `m.w.${address.toLowerCase()}`;
const NAME = "m.name"; // deviceFlag's wallet name — it drives the login emoji

export function switchWallet(from: string, to: string, prevCred?: unknown, ask = true): void {
  releasePusher(); // no late push of one wallet's settings to the other
  try {
    // 0. v1.1.9 — note what the browser opened with, before it's put aside.
    if (ask && prevCred) {
      const name = localStorage.getItem(NAME);
      const prev: PrevWallet = {
        address: from, cred: prevCred, name,
        emoji: getEmoji(name ?? from),
        label: cachedProfile(from)?.name ?? null,
      };
      localStorage.setItem(ASK_KEY, JSON.stringify(prev));
    } else if (!ask) {
      localStorage.removeItem(ASK_KEY);
    }
    // 1. Put the old wallet's keys aside.
    const out: Record<string, string> = {};
    for (const k of WALLET_KEYS) {
      const v = localStorage.getItem(k);
      if (v !== null) out[k] = v;
    }
    localStorage.setItem(STASH(from), JSON.stringify(out));
    for (const k of WALLET_KEYS) localStorage.removeItem(k);
    // The old wallet's name would paint ITS emoji over the new credential: the
    // name is rewritten at the home's login; until then, no name = the M.
    localStorage.removeItem(NAME);
    void clearNameIdb();

    // 2. Put the new wallet's keys back, if it was ever used in this browser.
    const raw = localStorage.getItem(STASH(to));
    if (raw) {
      const back = JSON.parse(raw) as Record<string, string>;
      for (const [k, v] of Object.entries(back)) {
        if ((WALLET_KEYS as readonly string[]).includes(k) && typeof v === "string") localStorage.setItem(k, v);
      }
      localStorage.removeItem(STASH(to));
    }
  } catch {
    /* storage blocked — nothing to keep apart */
  }
  // The theme was painted from the old wallet's value at boot. initTheme only
  // applies it — setTheme would stamp it as a fresh change and win the merge.
  try { initTheme(); } catch { /* cosmetic */ }
}

// deviceFlag mirrors m.name into IndexedDB; clear it there too, or the
// recovery path would restore the old wallet's name.
function clearNameIdb(): Promise<void> {
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open("m-device", 1);
      req.onupgradeneeded = () => req.result.createObjectStore("kv");
      req.onerror = () => resolve();
      req.onsuccess = () => {
        const db = req.result;
        try {
          const tx = db.transaction("kv", "readwrite");
          tx.objectStore("kv").delete(NAME);
          tx.oncomplete = () => { db.close(); resolve(); };
          tx.onerror = () => { db.close(); resolve(); };
        } catch {
          db.close();
          resolve();
        }
      };
    } catch {
      resolve();
    }
  });
}
